import {StyledAdornedEnd, StyledHelperTypography, StyledSpacerRow} from 'styles/InputMenu';

import InputActionButtons from './InputActionButtons';

import type * as I from 'interfaces';
import useAddEditCage from 'hooks/addEdit/useAddEditCage';
import useParseURL from 'hooks/useParseURL';

import {Box, Stack, styled} from '@mui/material';

import AddEditCageInitial from 'components/InputMenu/CageInitial';

import {handleKeyDown} from 'components/InputMenu/helpers';
import NumberFormatCustom from 'helpers/NumberFormatter';
import {useInputMenuContext} from 'context/InputMenu';
import {useGetSites} from 'hooks/swr';
import {Loading} from 'components/Loading';
import {isValidDate} from 'helpers/UnitsFormatHandler';
import GenericTextField from 'components/Generic/TextField';
import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import {useTranslation} from 'react-i18next';
import {type ChangeEvent} from 'react';

const AddEditCage = () => {
  const {planId} = useParseURL();

  const {data: sites} = useGetSites({resourceId: planId});
  const {t} = useTranslation('common');

  const {cage, setCage, setCreateUpdateCage, isLoading} = useAddEditCage();
  const inputMenuDispatch = useInputMenuContext()?.dispatch;
  const handleChange = (prop: keyof I.ICage) => (event: ChangeEvent<HTMLInputElement>) => {
    cage && setCage({...cage, [prop]: event.target.value});
  };

  if (!cage || isLoading) {
    return <Loading />;
  }

  const isCageInvalid = (Object.keys(cage) as Array<keyof I.ICage>).some((key) => validateCage(cage, key).length > 0);

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      <CageProperties initial={false} />
      {/* Site Picker */}
      <GenericSelect
        inputProps={{'data-testid': 'site'}}
        value={cage?.siteId}
        onChange={(event) => {
          const siteId = event.target.value as string;
          setCage({...cage, siteId});
        }}
        label={t('site')}
      >
        {sites?.map((site) => (
          <GenericSelectItem key={site.id} value={site.id}>
            {site.name}
          </GenericSelectItem>
        ))}
      </GenericSelect>
      <StyledSpacerRow />

      {/* Cage Name */}
      <GenericTextField
        inputProps={{'data-testid': 'name'}}
        label={t('name')}
        value={cage.name}
        onChange={handleChange('name')}
        onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
      />
      <StyledSpacerRow />

      {/* cage Volume */}
      <GenericTextField
        inputProps={{'data-testid': 'volume'}}
        InputProps={{
          inputComponent: NumberFormatCustom as (props) => JSX.Element,
          endAdornment: <StyledAdornedEnd>{t('m3')}</StyledAdornedEnd>,
        }}
        label={t('volume')}
        value={cage.volume > 0 ? cage.volume : ''}
        onChange={handleChange('volume')}
        onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
      />
      <StyledSpacerRow />

      {/* Cage Max Density */}
      <GenericTextField
        inputProps={{'data-testid': 'max-density'}}
        InputProps={{
          inputComponent: NumberFormatCustom as (props) => JSX.Element,
          endAdornment: <StyledAdornedEnd>{t('kgm3')}</StyledAdornedEnd>,
        }}
        label={t('maxDensity')}
        value={cage.maxDensity > 0 ? cage.maxDensity : ''}
        onChange={handleChange('maxDensity')}
        onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
      />
      <StyledSpacerRow />

      {/* Cage Max Count */}
      <GenericTextField
        inputProps={{'data-testid': 'max-count'}}
        InputProps={{
          inputComponent: NumberFormatCustom as (props) => JSX.Element,
          endAdornment: <StyledAdornedEnd>{t('fish')}</StyledAdornedEnd>,
        }}
        label={t('maxCount')}
        value={cage.maxCount > 0 ? cage.maxCount : ''}
        onChange={handleChange('maxCount')}
        onKeyPress={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCage)}
      />
      <StyledSpacerRow />

      <CageProperties initial={true} />
      <AddEditCageInitial cage={cage} setCage={setCage} setCreateUpdateCage={setCreateUpdateCage} />
      <StyledSpacerRow />
      <StyledSpacerRow />
      <InputActionButtons
        disabled={!isValidDate(cage.inputDate) || isCageInvalid}
        setCreateUpdateInput={() => {
          setCreateUpdateCage(true);
        }}
      />
    </form>
  );
};

const CageProperties = ({initial}: {initial: boolean}) => {
  const {t} = useTranslation('common');

  return (
    <Stack direction="row" justifyContent="space-between" pb={2}>
      <StyledHelperTypography fontSize="fontSize">
        {initial ? t('initialState') : t('cageProperties')}
      </StyledHelperTypography>
      <Box>
        {
          <StyledBox fontSize="fontSize" initial={initial}>
            {initial ? t('notRequired') : t('required')}
          </StyledBox>
        }
      </Box>
    </Stack>
  );
};

export const validateCage = (cage: I.ICage, key: keyof I.ICage): string => {
  switch (key) {
    case 'initialCount':
      if (cage.initialWeight > 0 && !cage.initialCount) {
        return 'cageCountRequired';
      }
      break;
    case 'initialWeight':
      if (cage.initialCount > 0 && !cage.initialWeight) {
        return 'cageWeightRequired';
      }
      break;
  }

  return '';
};

export default AddEditCage;

interface StyledBoxProps {
  initial: boolean;
}

const StyledBox = styled(Box)<StyledBoxProps>(({theme, initial}) => ({
  color: initial ? theme.palette.text.secondary : 'red',
}));
